// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Administration — titres des pages Admin, gestion des comptes utilisateurs.
//
// Les tarifs Mistral et la rentabilité sont dans `facturation.js`, les tableaux
// de consommation et de résultats dans `statistiques.js` : ces trois domaines
// cohabitaient dans une même section de `i18n.js`, sans que rien ne les sépare.
//
// ⚠️ `edit_role` et `edit_quota` ne sont référencées par aucun composant
// (vérifié le 27/07/2026). Conservées telles quelles — retirer des traductions
// est une décision produit, pas un nettoyage de découpe.

const admin = {
  fr: {
    admin_delete_confirm:            'Supprimer cet élément ?',
    admin_annotations_title:         'Annotations',
    admin_stats_title:               'Statistiques globales',
    admin_ratings_title:             'Evaluations utilisateurs',
    admin_suggestions_title:         'Suggestions utilisateurs',
    admin_users_title:               'Gestion utilisateurs',

    user_role:                       'Role',
    user_quota:                      'Quota',
    user_quota_used:                 'Utilise',
    user_last_activity:              'Derniere activite',
    user_created:                    'Cree le',
    user_delete_confirm:             'Supprimer cet utilisateur et toutes ses donnees ?',
    // Export RGPD par un administrateur (5 août 2026)
    user_export_rgpd:                'Données',
    user_export_rgpd_loading:        'Préparation…',
    user_export_rgpd_title:          'Télécharger les données personnelles de ce compte (RGPD Art. 20) — pour répondre à une demande reçue à privacy@evalens.fr',

    // ── Journal d'audit (5 août 2026) ──
    audit_title:                     'Journal d\'audit',
    audit_intro:                     'Connexions, changements de droits, factures et effacements. Conservé 12 mois, puis purgé automatiquement.',
    audit_filter_action:             'Action',
    audit_filter_all_actions:        'Toutes les actions',
    audit_filter_email:              'Email',
    audit_filter_email_ph:           'Auteur de l\'action',
    audit_filter_search:             'Recherche',
    audit_filter_search_ph:          'Dans le détail (ex. un email ciblé)',
    audit_filter_from:               'Du',
    audit_filter_to:                 'Au',
    audit_filter_status:             'Statut',
    audit_filter_all_status:         'Tous',
    audit_reset:                     'Réinitialiser',
    audit_col_date:                  'Date',
    audit_col_action:                'Action',
    audit_col_author:                'Auteur',
    audit_col_details:               'Détail',
    audit_col_ip:                    'IP',
    audit_col_status:                'Statut',
    audit_empty:                     'Aucun événement pour ces filtres.',
    audit_loading:                   'Chargement…',
    audit_count:                     'événements',
    audit_prev:                      'Précédent',
    audit_next:                      'Suivant',
    audit_page:                      'Page',
    user_count:                      'utilisateur',
    user_count_pl:                   'utilisateurs',
    edit_role:                       'Modifier le role',
    edit_quota:                      'Modifier le quota',

    users_section_forfait:           '💳 Forfaits self-service',
    users_section_licence:           '🏢 Licences entreprise',
    users_section_autres:            '👤 Autres comptes',
    users_empty_category:            'Aucun compte dans cette catégorie.',
    users_col_consommation:          'Consommation',
    users_consommation_month_suffix: 'ce mois',
    users_consommation_total_suffix: 'total',
    users_ocr_origine_compte:        'OCR activé pour ce compte',
    users_ocr_origine_licence:       'OCR inclus dans la licence',
    users_ocr_origine_forfait:       'OCR inclus dans le forfait',
    users_ocr_origine_aucune:        'Pas d’accès OCR',
    users_abo_resilie:               'Abonnement résilié',
    users_abo_impaye:                'Abonnement impayé',
    users_abo_expire:                'Souscription expirée',
    users_abo_retard:                'Paiement en retard',
    users_col_licence:               'Licence',
    users_col_pool:                  'Pool mensuel',
    users_col_pool_consumption:      'Consommation / pool',
    users_licence_managed_note:      'Rôle, forfait, quota et OCR sont gérés depuis Clients entreprise.',
    users_cabinet_gere_licences:     'Siège géré depuis Clients Entreprise',

    // ── Fiche client (chantier SaaS 5bis, 23/09/2026) ──
    fiche_ouvrir:                    'Ouvrir la fiche client',
    fiche_fermer:                    'Fermer la fiche',
    fiche_chargement:                'Chargement de la fiche…',
    fiche_onglet_admin:              'Administratif',
    fiche_onglet_abonnement:         'Abonnement',
    fiche_onglet_factures:           'Factures',
    fiche_onglet_conso:              'Consommation',
    fiche_non_renseigne:             'Non renseigné',
    fiche_oui:                       'Oui',
    fiche_non:                       'Non',
    fiche_section_compte:            'Compte',
    fiche_email:                     'E-mail de connexion',
    fiche_nom_affiche:               'Nom affiché',
    fiche_cree_le:                   'Compte créé le',
    fiche_etat:                      'État du compte',
    fiche_actif:                     'Actif',
    fiche_revoque_le:                'Révoqué le {date}',
    fiche_cgu:                       'Conditions acceptées',
    fiche_cgu_valeur:                'Version {v}, le {date}',
    fiche_cgu_jamais:                'Jamais acceptées',
    fiche_mfa:                       'Double authentification',
    fiche_licence:                   'Licence',
    fiche_section_identite:          'Identité de facturation',
    fiche_raison_sociale:            'Raison sociale',
    fiche_siren:                     'SIREN',
    fiche_siret:                     'SIRET',
    fiche_tva:                       'N° de TVA intracommunautaire',
    fiche_adresse:                   'Adresse',
    fiche_code_postal:               'Code postal',
    fiche_ville:                     'Ville',
    fiche_pays:                      'Pays',
    fiche_email_facturation:         'E-mail de facturation',
    fiche_telephone:                 'Téléphone',
    fiche_origine_stripe:            'Recopiée depuis le paiement Stripe : à vérifier.',
    fiche_origine_admin:             'Modifiée par {par} le {date}.',
    fiche_origine_client:            'Saisie par le client.',
    fiche_origine_licence:           'Identité de la licence : elle se modifie dans Clients entreprise.',
    fiche_source_titulaire:          'Siège d\'un Cabinet : identité du titulaire ({email}), à modifier depuis sa fiche.',
    fiche_siren_manquant:            'SIREN manquant : il sera indispensable pour la facturation électronique (1er septembre 2027).',
    fiche_modifier:                  'Modifier',
    fiche_enregistrer:               'Enregistrer',
    fiche_annuler:                   'Annuler',
    fiche_palier_actuel:             'Forfait actuel',
    fiche_statut:                    'Statut',
    fiche_fin_periode:               'Fin de la période en cours',
    fiche_aucun_palier:              'Aucun forfait',
    fiche_statut_active:             'Actif',
    fiche_statut_trialing:           'Essai',
    fiche_statut_none:               'Aucun abonnement',
    fiche_statut_incomplete:         'Paiement en attente',
    fiche_historique_titre:          'Historique de l\'abonnement',
    fiche_historique_vide:           'Aucun événement enregistré. L\'historique commence à la mise en service de cette fiche : un changement plus ancien n\'y figure pas.',
    fiche_historique_titulaire:      'Historique du Cabinet : c\'est l\'abonnement du titulaire.',
    fiche_abo_licence:               'Siège de la licence {nom} : pas d\'abonnement individuel. Le contrat se gère dans Clients entreprise.',
    fiche_col_date:                  'Date',
    fiche_col_evenement:             'Événement',
    fiche_col_detail:                'Détail',
    fiche_col_origine:               'Origine',
    fiche_evt_souscription:          'Souscription',
    fiche_evt_reabonnement:          'Réabonnement',
    fiche_evt_montee_palier:         'Montée de forfait',
    fiche_evt_descente_palier:       'Descente de forfait',
    fiche_evt_changement_palier:     'Changement de forfait',
    fiche_evt_changement_statut:     'Changement de statut',
    fiche_evt_resiliation_programmee: 'Résiliation programmée',
    fiche_evt_resiliation_levee:     'Résiliation annulée',
    fiche_evt_resiliation:           'Résiliation',
    fiche_fin_prevue:                'fin prévue le {date}',
    fiche_origine_evt_stripe:        'Stripe',
    fiche_origine_evt_client:        'Retour du client après paiement',
    fiche_origine_evt_resync:        'Resynchronisation avec Stripe',
    fiche_origine_evt_admin:         'Evalens ({email})',
    fiche_factures_vide:             'Aucune facture.',
    fiche_factures_titulaire:        'Les factures d\'un Cabinet sont au nom de son titulaire ({email}).',
    fiche_factures_qonto:            'Factures de la licence (QONTO)',
    fiche_col_numero:                'N°',
    fiche_col_periode:               'Période',
    fiche_col_ht:                    'HT',
    fiche_col_ttc:                   'TTC',
    fiche_col_paye:                  'Payé',
    fiche_col_pdf:                   'Facture',
    fiche_voir_facture:              'Ouvrir',
    fiche_facture_paid:              'Payée',
    fiche_facture_open:              'À payer',
    fiche_facture_void:              'Annulée',
    fiche_facture_draft:             'Brouillon',
    fiche_facture_uncollectible:     'Irrécouvrable',
    fiche_conso_total:               '{n} analyse(s) au total',
    fiche_conso_par_mois:            'Analyses par mois',
    fiche_conso_liste:               'Analyses (les {n} plus récentes)',
    fiche_conso_vide:                'Aucune analyse.',
    fiche_col_fichier:               'Fichier',
    fiche_col_depose:                'Déposée le',
    fiche_analyse_terminee:          'Terminée',
    fiche_analyse_erreur:            'En erreur',
    fiche_analyse_en_cours:          'En cours',
    fiche_hf_titre:                  'Analyses hors forfait transmises à Stripe',
    fiche_hf_vide:                   'Aucune analyse hors forfait.',
    fiche_col_rang:                  'Rang dans le mois',
    fiche_col_prix:                  'Prix unitaire',
    fiche_hf_envoye:                 'Facturée',
    fiche_hf_echec:                  'Envoi en échec',
    fiche_hf_en_attente:             'En attente',
    fiche_pool_mois:                 'Mois',
    fiche_pool_indisponible:         'Pool partagé indisponible pour ce mois.',
    fiche_pool_deplace:              'Le pool partagé de chaque Cabinet (hors-forfait facturé, marge) se consulte dans sa fiche client : cliquez sur l\'e-mail du titulaire.',

    // Résumé d'utilisation (page AdminUsers)
    summary_title:                   '📊 Résumé d\'utilisation',
    summary_mode_user:               'Par user',
    summary_mode_forfait:            'Par forfait',
    summary_mode_licence:            'Par licence',
    summary_pick_user:               'Sélectionnez une valeur pour afficher le résumé.',
  },

  en: {
    admin_delete_confirm:            'Delete this item?',
    admin_annotations_title:         'Annotations',
    admin_stats_title:               'Global statistics',
    admin_ratings_title:             'User ratings',
    admin_suggestions_title:         'User suggestions',
    admin_users_title:               'User management',

    user_role:                       'Role',
    user_quota:                      'Quota',
    user_quota_used:                 'Used',
    user_last_activity:              'Last activity',
    user_created:                    'Created',
    user_delete_confirm:             'Delete this user and all their data?',
    // GDPR export by an administrator (5 August 2026)
    user_export_rgpd:                'Data',
    user_export_rgpd_loading:        'Preparing…',
    user_export_rgpd_title:          'Download this account\'s personal data (GDPR Art. 20) — to answer a request received at privacy@evalens.fr',

    // ── Audit log (5 August 2026) ──
    audit_title:                     'Audit log',
    audit_intro:                     'Sign-ins, permission changes, invoices and erasures. Kept for 12 months, then purged automatically.',
    audit_filter_action:             'Action',
    audit_filter_all_actions:        'All actions',
    audit_filter_email:              'Email',
    audit_filter_email_ph:           'Who performed the action',
    audit_filter_search:             'Search',
    audit_filter_search_ph:          'In details (e.g. a targeted email)',
    audit_filter_from:               'From',
    audit_filter_to:                 'To',
    audit_filter_status:             'Status',
    audit_filter_all_status:         'All',
    audit_reset:                     'Reset',
    audit_col_date:                  'Date',
    audit_col_action:                'Action',
    audit_col_author:                'Author',
    audit_col_details:               'Details',
    audit_col_ip:                    'IP',
    audit_col_status:                'Status',
    audit_empty:                     'No events for these filters.',
    audit_loading:                   'Loading…',
    audit_count:                     'events',
    audit_prev:                      'Previous',
    audit_next:                      'Next',
    audit_page:                      'Page',
    user_count:                      'user',
    user_count_pl:                   'users',
    edit_role:                       'Edit role',
    edit_quota:                      'Edit quota',

    users_section_forfait:           '💳 Self-service plans',
    users_section_licence:           '🏢 Enterprise licenses',
    users_section_autres:            '👤 Other accounts',
    users_empty_category:            'No account in this category.',
    users_col_consommation:          'Usage',
    users_consommation_month_suffix: 'this month',
    users_consommation_total_suffix: 'total',
    users_ocr_origine_compte:        'OCR enabled for this account',
    users_ocr_origine_licence:       'OCR included in the license',
    users_ocr_origine_forfait:       'OCR included in the plan',
    users_ocr_origine_aucune:        'No OCR access',
    users_abo_resilie:               'Subscription cancelled',
    users_abo_impaye:                'Subscription unpaid',
    users_abo_expire:                'Signup expired',
    users_abo_retard:                'Payment overdue',
    users_col_licence:               'License',
    users_col_pool:                  'Monthly pool',
    users_col_pool_consumption:      'Usage / pool',
    users_licence_managed_note:      'Role, plan, quota and OCR are managed from Enterprise Clients.',
    users_cabinet_gere_licences:     'Seat managed from Enterprise Clients',

    // ── Customer record (SaaS project 5bis, 23/09/2026) ──
    fiche_ouvrir:                    'Open customer record',
    fiche_fermer:                    'Close record',
    fiche_chargement:                'Loading record…',
    fiche_onglet_admin:              'Administrative',
    fiche_onglet_abonnement:         'Subscription',
    fiche_onglet_factures:           'Invoices',
    fiche_onglet_conso:              'Usage',
    fiche_non_renseigne:             'Not provided',
    fiche_oui:                       'Yes',
    fiche_non:                       'No',
    fiche_section_compte:            'Account',
    fiche_email:                     'Login e-mail',
    fiche_nom_affiche:               'Display name',
    fiche_cree_le:                   'Account created on',
    fiche_etat:                      'Account status',
    fiche_actif:                     'Active',
    fiche_revoque_le:                'Revoked on {date}',
    fiche_cgu:                       'Terms accepted',
    fiche_cgu_valeur:                'Version {v}, on {date}',
    fiche_cgu_jamais:                'Never accepted',
    fiche_mfa:                       'Two-factor authentication',
    fiche_licence:                   'License',
    fiche_section_identite:          'Billing identity',
    fiche_raison_sociale:            'Company name',
    fiche_siren:                     'SIREN',
    fiche_siret:                     'SIRET',
    fiche_tva:                       'EU VAT number',
    fiche_adresse:                   'Address',
    fiche_code_postal:               'Postal code',
    fiche_ville:                     'City',
    fiche_pays:                      'Country',
    fiche_email_facturation:         'Billing e-mail',
    fiche_telephone:                 'Phone',
    fiche_origine_stripe:            'Copied from the Stripe payment: to be checked.',
    fiche_origine_admin:             'Edited by {par} on {date}.',
    fiche_origine_client:            'Entered by the customer.',
    fiche_origine_licence:           'License identity: edit it in Enterprise Clients.',
    fiche_source_titulaire:          'Cabinet seat: identity of the owner ({email}), edit it from their record.',
    fiche_siren_manquant:            'Missing SIREN: it will be required for e-invoicing (1 September 2027).',
    fiche_modifier:                  'Edit',
    fiche_enregistrer:               'Save',
    fiche_annuler:                   'Cancel',
    fiche_palier_actuel:             'Current plan',
    fiche_statut:                    'Status',
    fiche_fin_periode:               'End of current period',
    fiche_aucun_palier:              'No plan',
    fiche_statut_active:             'Active',
    fiche_statut_trialing:           'Trial',
    fiche_statut_none:               'No subscription',
    fiche_statut_incomplete:         'Payment pending',
    fiche_historique_titre:          'Subscription history',
    fiche_historique_vide:           'No event recorded. History starts when this record went live: older changes are not listed.',
    fiche_historique_titulaire:      'Cabinet history: this is the owner\'s subscription.',
    fiche_abo_licence:               'Seat of license {nom}: no individual subscription. The contract is managed in Enterprise Clients.',
    fiche_col_date:                  'Date',
    fiche_col_evenement:             'Event',
    fiche_col_detail:                'Detail',
    fiche_col_origine:               'Source',
    fiche_evt_souscription:          'Subscription',
    fiche_evt_reabonnement:          'Resubscription',
    fiche_evt_montee_palier:         'Plan upgrade',
    fiche_evt_descente_palier:       'Plan downgrade',
    fiche_evt_changement_palier:     'Plan change',
    fiche_evt_changement_statut:     'Status change',
    fiche_evt_resiliation_programmee: 'Cancellation scheduled',
    fiche_evt_resiliation_levee:     'Cancellation withdrawn',
    fiche_evt_resiliation:           'Cancellation',
    fiche_fin_prevue:                'ends on {date}',
    fiche_origine_evt_stripe:        'Stripe',
    fiche_origine_evt_client:        'Customer back from payment',
    fiche_origine_evt_resync:        'Resynchronised with Stripe',
    fiche_origine_evt_admin:         'Evalens ({email})',
    fiche_factures_vide:             'No invoice.',
    fiche_factures_titulaire:        'A Cabinet\'s invoices are issued to its owner ({email}).',
    fiche_factures_qonto:            'License invoices (QONTO)',
    fiche_col_numero:                'No.',
    fiche_col_periode:               'Period',
    fiche_col_ht:                    'Excl. VAT',
    fiche_col_ttc:                   'Incl. VAT',
    fiche_col_paye:                  'Paid',
    fiche_col_pdf:                   'Invoice',
    fiche_voir_facture:              'Open',
    fiche_facture_paid:              'Paid',
    fiche_facture_open:              'Due',
    fiche_facture_void:              'Void',
    fiche_facture_draft:             'Draft',
    fiche_facture_uncollectible:     'Uncollectible',
    fiche_conso_total:               '{n} analysis(es) in total',
    fiche_conso_par_mois:            'Analyses per month',
    fiche_conso_liste:               'Analyses (latest {n})',
    fiche_conso_vide:                'No analysis.',
    fiche_col_fichier:               'File',
    fiche_col_depose:                'Uploaded on',
    fiche_analyse_terminee:          'Completed',
    fiche_analyse_erreur:            'Failed',
    fiche_analyse_en_cours:          'In progress',
    fiche_hf_titre:                  'Over-plan analyses sent to Stripe',
    fiche_hf_vide:                   'No over-plan analysis.',
    fiche_col_rang:                  'Rank in the month',
    fiche_col_prix:                  'Unit price',
    fiche_hf_envoye:                 'Billed',
    fiche_hf_echec:                  'Sending failed',
    fiche_hf_en_attente:             'Pending',
    fiche_pool_mois:                 'Month',
    fiche_pool_indisponible:         'Shared pool unavailable for this month.',
    fiche_pool_deplace:              'Each Cabinet\'s shared pool (over-plan billed, margin) is shown in its customer record: click the owner\'s e-mail.',

    // Usage summary (AdminUsers page)
    summary_title:                   '📊 Usage summary',
    summary_mode_user:               'By user',
    summary_mode_forfait:            'By plan',
    summary_mode_licence:            'By license',
    summary_pick_user:               'Select a value to display the summary.',
  },
};

export default admin;
